import React, { useState, useCallback, useEffect, useMemo, useRef, useImperativeHandle } from 'react';
import { Button, Pagination, MonthYearPicker } from 'components/common';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import { FaTimes, FaSearch, FaSpinner, FaPlus } from 'react-icons/fa';
import { MdOutlineImportExport, MdBusiness, MdHistory } from 'react-icons/md';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { importCsdFromSource, getEligibleBuildings, getBuildingStatuses } from 'redux/crm/releaseForecastSlice';
import apiAxios from 'helpers/apiAxios';
import { objectSerializer } from 'helpers/utils';

const BASE_CSD_URL   = '/crm/release_forecast/csds';
const BASE_SALES_URL = '/crm/release_forecast/sales';

// ── helpers ────────────────────────────────────────────────────────────────────

const getMonthOptions = (period) => {
  if (!period) return [];
  const base = dayjs(period, 'MMM-YYYY');
  if (!base.isValid()) return [];
  return [1, 2, 3].map((i) => ({
    index:    i,
    label:    base.add(i, 'month').format('MMM-YYYY'),
    firstDay: base.add(i, 'month').startOf('month').format('YYYY-MM-DD'),
  }));
};

const prevPeriod = (period) => {
  const base = dayjs(period, 'MMM-YYYY');
  return base.isValid() ? base.subtract(1, 'month').format('MMM-YYYY') : period;
};

// ── expand API records → flat building rows ────────────────────────────────────

// importedIds: buildings the target period's CSD already holds — left out, so an
// import never overwrites them (their siblings stay importable).
export const expandSales = (sales, monthOptions, importedIds = []) =>
  sales.flatMap((sale) => {
    const stub = {
      deal_id:         sale.project?.id,
      project_name:    sale.project?.name || '—',
      job_no:          sale.project?.j_number || sale.project?.q_number || '—',
      sent_at:         sale.sent_at ? dayjs(sale.sent_at).format('DD-MMM-YYYY') : '—',
      sales_incharge:       sale.project?.sales_incharge || '—',
      sales_unit:           sale.sales_unit?.name || '—',
      sales_unit_id:        sale.sales_unit?.id ?? null,
      project_status_name:  sale.project_status_name || '—',
    };
    const imported = new Set(importedIds.map(String));
    const all = sale.buildings || [];
    const buildings = all.filter((b) => !imported.has(String(b.id)));
    if (all.length && !buildings.length) return [];
    if (!buildings.length) {
      return [{ ...stub, id: `s${sale.id}-stub`, building_id: null, building_name: '—',
        month_index: monthOptions[0]?.index ?? 1, built_up_weight: 0 }];
    }
    return buildings.map((b) => ({
      ...stub,
      id:              `s${sale.id}-b${b.id}`,
      building_id:     b.id,
      building_name:   b.name || '—',
      month_index:     monthOptions.find((m) => m.index === Number(b.month))?.index ?? monthOptions[0]?.index ?? 1,
      built_up_weight: Number(b.weight) || 0,
    }));
  });

const expandCsds = (csds, monthOptions) =>
  csds.flatMap((csd) => {
    const stub = {
      deal_id:        csd.project?.id,
      project_name:   csd.project?.name || '—',
      job_no:         csd.project?.j_number || csd.project?.q_number || '—',
      sales_incharge:      csd.project?.sales_incharge || '—',
      sales_unit:          csd.sales_unit?.name || '—',
      sales_unit_id:       csd.sales_unit?.id ?? null,
    };
    const buildings = csd.buildings || [];
    if (!buildings.length) {
      return [{ ...stub, id: `c${csd.id}-stub`, building_id: null, building_name: '—',
        month_index: monthOptions[0]?.index ?? 1, built_up_weight: 0, cold_formed_weight: 0, sheeting_weight: 0 }];
    }
    return buildings.flatMap((b) => {
      const months = (b.months || []).filter(
        (m) => (m.built_up_weight || 0) + (m.cold_formed_weight || 0) + (m.sheeting_weight || 0) > 0,
      );
      const bStatus = b.project_status_name || '—';
      if (!months.length) {
        return [{ ...stub, id: `c${csd.id}-b${b.building_id}-stub`, building_id: b.building_id,
          building_name: b.building_name || '—', month_index: monthOptions[0]?.index ?? 1,
          built_up_weight: 0, cold_formed_weight: 0, sheeting_weight: 0,
          project_status_name: bStatus }];
      }
      return months.map((m) => ({
        ...stub,
        id:                  `c${csd.id}-b${b.building_id}-m${m.month_index}`,
        building_id:         b.building_id,
        building_name:       b.building_name || '—',
        month_index:         m.month_index,
        built_up_weight:     m.built_up_weight   || 0,
        cold_formed_weight:  m.cold_formed_weight || 0,
        sheeting_weight:     m.sheeting_weight   || 0,
        project_status_name: bStatus,
      }));
    });
  });

// ── shared UI atoms ────────────────────────────────────────────────────────────

const MonthSelectCell = ({ value, options, onChange }) => (
  <select
    value={value}
    onChange={(e) => onChange(Number(e.target.value))}
    onClick={(e) => e.stopPropagation()}
    style={{ fontSize: 11, padding: '1px 4px', border: '1px solid #d1d5db', borderRadius: 3, background: '#fff', width: '100%', outline: 'none' }}
  >
    {options.map((o) => <option key={o.index} value={o.index}>{o.label}</option>)}
  </select>
);
MonthSelectCell.propTypes = {
  value: PropTypes.number.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    index: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    firstDay: PropTypes.string.isRequired,
  })).isRequired,
  onChange: PropTypes.func.isRequired,
};

const MultiStatusSelect = ({ value = [], onChange, statuses }) => {
  const [open, setOpen] = useState(false);
  const [pos,  setPos]  = useState({ top: 0, left: 0, width: 0 });
  const triggerRef      = useRef(null);

  useEffect(() => {
    if (!open) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 200) });
    const handler = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle  = (name) => onChange(value.includes(name) ? value.filter((v) => v !== name) : [...value, name]);
  const label   = value.length === 0 ? 'All statuses' : value.length === 1 ? value[0] : `${value.length} selected`;
  const hasVal  = value.length > 0;

  return (
    <div ref={triggerRef} className="tw-relative tw-shrink-0">
      <div className="tw-flex tw-items-center tw-border tw-border-gray-300 tw-rounded-sm tw-bg-white tw-text-xs tw-cursor-pointer hover:tw-border-primary-400 tw-transition-colors">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="tw-px-2 tw-py-1.5 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-xs tw-text-gray-700 tw-whitespace-nowrap tw-min-w-[130px] tw-text-left"
        >
          {label}
        </button>
        {hasVal && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange([]); }}
            className="tw-px-1.5 tw-py-1 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-gray-400 hover:tw-text-gray-600 tw-border-l tw-border-gray-200"
            title="Clear filter"
          >
            <FaTimes size={9} />
          </button>
        )}
        <span className="tw-px-1.5 tw-text-gray-400 tw-text-xs tw-border-l tw-border-gray-200 tw-py-1.5 tw-select-none">▾</span>
      </div>

      {open && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 99999 }}
          className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-sm tw-shadow-xl tw-max-h-56 tw-overflow-y-auto"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {statuses.length === 0 && (
            <div className="tw-px-3 tw-py-2 tw-text-gray-400 tw-text-xs">Loading…</div>
          )}
          {statuses.map((s) => (
            <label
              key={s.id}
              className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-cursor-pointer hover:tw-bg-blue-50 tw-text-xs ${
                value.includes(s.name) ? 'tw-bg-blue-50 tw-text-blue-700 tw-font-medium' : 'tw-text-gray-700'
              }`}
            >
              <input
                type="checkbox"
                checked={value.includes(s.name)}
                onChange={() => toggle(s.name)}
                className="tw-accent-primary-600"
              />
              {s.name}
            </label>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};
MultiStatusSelect.propTypes = {
  value:    PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  statuses: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })).isRequired,
};

const SearchInput = ({ value, onChange, onEnter, placeholder }) => (
  <div className="tw-flex tw-items-center tw-border tw-border-gray-300 tw-rounded-sm tw-bg-gray-50 tw-flex-1">
    <span className="tw-px-2 tw-text-gray-400"><FaSearch size={11} /></span>
    <input
      type="text" value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => e.key === 'Enter' && onEnter()}
      className="tw-flex-1 tw-border-0 tw-outline-none tw-bg-transparent tw-text-sm tw-py-1.5 tw-pr-2"
    />
    {value && (
      <button onClick={() => { onChange(''); onEnter(''); }} className="tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-gray-400 tw-px-1.5 hover:tw-text-gray-600">
        <FaTimes size={9} />
      </button>
    )}
  </div>
);
SearchInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onEnter: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

const SearchBtn = ({ onClick }) => (
  <Button variant="primary" size="sm" leftIcon={FaSearch} onClick={onClick}>
    Search
  </Button>
);
SearchBtn.propTypes = { onClick: PropTypes.func.isRequired };

const GridOverlay = ({ loading }) =>
  loading ? (
    <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white/80 tw-z-10">
      <span className="tw-text-sm tw-text-primary-600 tw-flex tw-items-center tw-gap-1.5">
        <FaSpinner className="tw-animate-spin" size={13} /> Loading…
      </span>
    </div>
  ) : null;
GridOverlay.propTypes = { loading: PropTypes.bool.isRequired };

// ── shared AG Grid defaults ────────────────────────────────────────────────────

const GRID_DEFAULTS = {
  defaultColDef: { sortable: false, resizable: true, filter: false, suppressMovable: true },
  rowSelection: 'multiple',
  suppressRowClickSelection: true,
  animateRows: true,
  rowHeight: 30,
  headerHeight: 36,
  overlayNoRowsTemplate: '<span style="color:#94a3b8;font-size:13px">No data found</span>',
};

const checkboxColDef = {
  headerName: '', width: 40, minWidth: 40, flex: 0,
  checkboxSelection: true, headerCheckboxSelection: true,
  sortable: false, filter: false, resizable: false, suppressMovable: true,
  cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
};

const rowNumColDef = {
  headerName: 'No.', width: 50, minWidth: 50, flex: 0,
  valueGetter: (p) => p.node.rowIndex + 1,
  sortable: false, filter: false, resizable: false, suppressMovable: true,
  cellStyle: { color: '#94a3b8', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' },
};

// ── Tab 1: Sales Released Tab ─────────────────────────────────────────────────────

const SalesReleasedTab = React.forwardRef(({ period, monthOptions, onCountChange, buildingStatuses }, ref) => {
  const gridRef = useRef(null);

  const [salePeriod,    setSalePeriod]    = useState(period);
  const [inputSearch,   setInputSearch]   = useState('');
  const [statusFilter,  setStatusFilter]  = useState([]);
  const [rowData,       setRowData]       = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [page,          setPage]          = useState(1);
  const [perPage,       setPerPage]       = useState(20);
  const [totalRows,     setTotalRows]     = useState(0);

  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  const fetchData = useCallback(async ({ p, pp, q = '', per, status = [] }) => {
    setLoading(true);
    try {
      const params = { sent: true, page: p, per_page: pp, target_period: period };
      if (per)           params.period              = per;
      if (q.trim())      params.q                   = q.trim();
      if (status.length) params.project_status_name = status.join(',');
      const res   = await apiAxios.get(BASE_SALES_URL, { params });
      const sales = objectSerializer(res.data?.rows || []);
      setRowData(expandSales(sales, monthOptions, res.data?.imported_building_ids));
      setTotalRows(res.data?.total_rows ?? sales.length);
    } catch {
      toast.error('Failed to load Sales Confirmed');
    } finally {
      setLoading(false);
    }
  }, [monthOptions, period]);

  useEffect(() => {
    setPage(1); setInputSearch(''); setStatusFilter([]);
    fetchData({ p: 1, pp: perPage, q: '', per: salePeriod, status: [] });
  // refetch from page 1 only when the period picker changes; paging/search fetch on their own
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salePeriod]);

  useImperativeHandle(ref, () => ({
    getSelectedRows: () => gridRef.current?.api?.getSelectedRows() ?? [],
  }));

  const handleSearch = () => {
    setPage(1);
    fetchData({ p: 1, pp: perPage, q: inputSearch, per: salePeriod, status: statusFilter });
  };

  const handleStatusChange = (val) => {
    setStatusFilter(val); setPage(1);
    fetchData({ p: 1, pp: perPage, q: inputSearch, per: salePeriod, status: val });
  };

  const handlePageChange = (p) => {
    setPage(p);
    fetchData({ p, pp: perPage, q: inputSearch, per: salePeriod, status: statusFilter });
  };

  const handlePerPageChange = (pp) => {
    setPerPage(pp); setPage(1);
    fetchData({ p: 1, pp, q: inputSearch, per: salePeriod, status: statusFilter });
  };

  const updateRowMonth = useCallback((rowId, monthIdx) => {
    setRowData((prev) => prev.map((r) => r.id === rowId ? { ...r, month_index: monthIdx } : r));
  }, []);

  const columnDefs = useMemo(() => [
    checkboxColDef, rowNumColDef,
    { headerName: 'Project Name',    field: 'project_name',        flex: 2, minWidth: 180 },
    { headerName: 'Job / Q No.',     field: 'job_no',               width: 110 },
    { headerName: 'Building',        field: 'building_name',        flex: 1, minWidth: 90 },
    { headerName: 'Status',          field: 'project_status_name',  width: 140 },
    {
      headerName: 'Month', field: 'month_index', width: 130,
      cellRenderer: (p) => (
        <MonthSelectCell value={p.data.month_index} options={monthOptions}
          onChange={(v) => updateRowMonth(p.data.id, v)} />
      ),
    },
    { headerName: 'Weight',    field: 'built_up_weight', width: 85, type: 'numericColumn',
      valueFormatter: (p) => p.value ? Number(p.value).toFixed(2) : '—' },
    { headerName: 'Sent Date', field: 'sent_at',         width: 105 },
    { headerName: 'Sales Unit', field: 'sales_unit',     width: 110 },
  ], [monthOptions, updateRowMonth]);

  return (
    <div className="tw-flex tw-flex-col tw-flex-1 tw-gap-2 tw-min-h-0">
      <div className="tw-flex tw-gap-2 tw-items-center tw-flex-wrap tw-shrink-0">
        <MonthYearPicker value={salePeriod} onChange={setSalePeriod} clearable minWidth={130} />
        <MultiStatusSelect value={statusFilter} onChange={handleStatusChange} statuses={buildingStatuses} />
        <SearchInput value={inputSearch} onChange={setInputSearch} onEnter={handleSearch}
          placeholder="Project name, job or quote number…" />
        <SearchBtn onClick={handleSearch} />
      </div>

      <div className="ag-theme-quartz tw-flex-1 tw-min-h-0" style={{ position: 'relative' }}>
        <GridOverlay loading={loading} />
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          getRowId={(p) => p.data.id}
          onSelectionChanged={(e) => onCountChange(e.api.getSelectedRows().length)}
          {...GRID_DEFAULTS}
        />
      </div>

      <div className="tw-shrink-0">
        <Pagination
          currentPage={page} totalPages={totalPages} totalResults={totalRows} perPage={perPage}
          onPageChange={handlePageChange} onPageSizeChange={handlePerPageChange}
          pageSizeOptions={[20, 50, 100]} disabled={loading}
        />
      </div>
    </div>
  );
});
SalesReleasedTab.propTypes = {
  period: PropTypes.string.isRequired,
  monthOptions: PropTypes.arrayOf(PropTypes.shape({
    index: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    firstDay: PropTypes.string.isRequired,
  })).isRequired,
  onCountChange:    PropTypes.func.isRequired,
  buildingStatuses: PropTypes.array.isRequired,
};

// ── Tab 2: All Projects Tab (Enterprise grouped) ─────────────────────────────────────

const AllProjectsTab = React.forwardRef(({ period, monthOptions, onCountChange, buildingStatuses }, ref) => {
  const dispatch       = useDispatch();
  const buildingsState = useSelector((s) => s.crm_release_forecast.buildingsData);
  const gridRef        = useRef(null);

  const [search,        setSearch]        = useState('');
  const [statusFilter,  setStatusFilter]  = useState([]);
  const [page,          setPage]          = useState(1);
  const [perPage,       setPerPage]       = useState(50);
  const [loading,       setLoading]       = useState(false);
  const [monthMap,      setMonthMap]      = useState({});

  const totalRows  = buildingsState.allData?.totalRows || 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));


  useImperativeHandle(ref, () => ({
    getSelectedItems: () => {
      const nodes = gridRef.current?.api?.getSelectedNodes() ?? [];
      return nodes
        .filter((n) => !n.group && n.data?.project_id)
        .map((n) => {
          const d   = n.data;
          const idx = monthMap[d.id] ?? monthOptions[0]?.index ?? 1;
          return {
            deal_id:         d.project_id,
            building_id:     d.id,
            month_index:     idx,
            built_up_weight: parseFloat(d.est_weight) || 0,
            cold_formed_weight: 0, sheeting_weight: 0,
            trans_date:      monthOptions.find((m) => m.index === idx)?.firstDay,
            sales_unit_id:   d.sales_unit_id ?? null,
          };
        });
    },
  }), [monthMap, monthOptions]);

  const doFetch = useCallback(({ p = 1, pp = perPage, q = search, status = statusFilter } = {}) => {
    setLoading(true);
    // for_csd: list every building of a project (CSD imports whole projects).
    const params = { page: p, per_page: pp, period, for_csd: true };
    if (q.trim())      params.general_search      = q.trim();
    if (status.length) params.project_status_name = status.join(',');
    dispatch(getEligibleBuildings(params)).finally(() => setLoading(false));
  }, [dispatch, period, perPage, search, statusFilter]);

  // Page / page-size changes keep period, search and status (they used to be dropped).
  useEffect(() => {
    doFetch({ p: page, pp: perPage });
  // paging only — search/status changes fetch from their own handlers, so doFetch's identity must not retrigger
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  const rowData = useMemo(() => {
    const items = buildingsState.allData?.data || [];
    return items.map((item) => ({
      id: item.id, name: item.name, j_number: item.j_number,
      est_weight: item.est_released_weight, building_status: item.building_status?.name,
      project_id: item.project?.id, project_name: item.project?.name,
      q_number:   item.project?.q_number, sales_unit_name: item.project?.sales_unit_name,
      sales_unit_id:        item.project?.sales_unit_id ?? null,
      project_status_name:  item.project?.project_status_name || '—',
      project_created_at:   item.project?.created_at,
    }));
  }, [buildingsState.allData]);

  const handleSearch = () => { setPage(1); doFetch({ p: 1, q: search, status: statusFilter }); };

  const handleStatusChange = (val) => {
    setStatusFilter(val); setPage(1);
    doFetch({ p: 1, q: search, status: val });
  };

  const columnDefs = useMemo(() => [
    { headerName: 'Building', field: 'name', flex: 2, minWidth: 140 },
    { headerName: 'Quote/Job', field: 'j_number', width: 110,
      valueGetter: (p) => p.node?.group ? (p.node.allLeafChildren?.[0]?.data?.q_number || '') : (p.data?.j_number || '') },
    { headerName: 'Status', field: 'project_status_name', width: 140,
      cellRenderer: (p) => !p.node?.group ? (p.value || '') : (p.node.allLeafChildren?.[0]?.data?.project_status_name || null) },
    { headerName: 'Est. Weight', field: 'est_weight', width: 100, type: 'numericColumn', aggFunc: 'sum',
      valueGetter: (p) => p.node?.group ? undefined : (parseFloat(p.data?.est_weight) || 0),
      valueFormatter: (p) => p.value != null ? Number(p.value).toFixed(2) : '—',
      cellStyle: (p) => p.node?.group ? { textAlign: 'right', fontWeight: 600, color: '#1d4ed8' } : { textAlign: 'right' } },
    { headerName: 'Month', field: 'month_assign', width: 130, sortable: false,
      cellRenderer: (p) => {
        if (p.node?.group) return null;
        const idx = monthMap[p.data?.id] ?? monthOptions[0]?.index ?? 1;
        return <MonthSelectCell value={idx} options={monthOptions}
          onChange={(v) => setMonthMap((prev) => ({ ...prev, [p.data.id]: v }))} />;
      } },
    { field: 'project_name', rowGroup: true, hide: true },
    { field: 'project_created_at', hide: true },
    { headerName: 'Sales Unit', field: 'sales_unit_name', width: 110,
      cellRenderer: (p) => !p.node?.group ? null : (p.node.allLeafChildren?.[0]?.data?.sales_unit_name || null) },
  ], [monthOptions, monthMap]);

  const autoGroupColumnDef = useMemo(() => ({
    headerName: 'Project / Building', minWidth: 320,
    headerCheckboxSelection: true,
    cellRendererParams: { checkbox: true, innerRenderer: (p) => {
      if (!p.node?.group) return <span>{p.value}</span>;
      const count = p.node.allLeafChildren?.length ?? 0;
      return <span className="tw-font-semibold">{p.value} <span className="tw-text-gray-400 tw-text-xs">({count})</span></span>;
    }},
  }), []);

  return (
    <div className="tw-flex tw-flex-col tw-flex-1 tw-gap-2 tw-min-h-0">
      <div className="tw-flex tw-gap-2 tw-items-center tw-flex-wrap tw-shrink-0">
        <MultiStatusSelect value={statusFilter} onChange={handleStatusChange} statuses={buildingStatuses} />
        <SearchInput value={search} onChange={setSearch} onEnter={handleSearch}
          placeholder="Project name, quote or job number…" />
        <SearchBtn onClick={handleSearch} />
        <span className="tw-text-xs tw-text-gray-400 tw-whitespace-nowrap">{totalRows} buildings</span>
      </div>

      <div className="ag-theme-quartz tw-flex-1 tw-min-h-0" style={{ position: 'relative' }}>
        <GridOverlay loading={loading} />
        <AgGridReact
          ref={gridRef}
          rowData={rowData} columnDefs={columnDefs} autoGroupColumnDef={autoGroupColumnDef}
          rowSelection="multiple" groupSelectsChildren groupDisplayType="singleColumn"
          groupDefaultExpanded={-1} suppressRowClickSelection suppressAggFuncInHeader animateRows
          onSelectionChanged={(e) => {
            const count = (e.api.getSelectedNodes() || []).filter((n) => !n.group).length;
            onCountChange(count);
          }}
          getRowId={(p) => String(p.data.id)}
          rowHeight={28} headerHeight={34} groupHeaderHeight={28}
          overlayNoRowsTemplate='<span style="color:#94a3b8;font-size:13px">No eligible buildings</span>'
        />
      </div>

      <div className="tw-shrink-0">
        <Pagination
          currentPage={page} totalPages={totalPages} totalResults={totalRows} perPage={perPage}
          onPageChange={setPage} onPageSizeChange={(pp) => { setPerPage(pp); setPage(1); }}
          pageSizeOptions={[20, 50, 100]} disabled={loading}
        />
      </div>
    </div>
  );
});

AllProjectsTab.propTypes = {
  period:       PropTypes.string.isRequired,
  monthOptions: PropTypes.arrayOf(PropTypes.shape({
    index: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    firstDay: PropTypes.string.isRequired,
  })).isRequired,
  onCountChange:    PropTypes.func.isRequired,
  buildingStatuses: PropTypes.array.isRequired,
};

// ── Tab 3: Previous Month Released Tab ──────────────────────────────────────────────────

const PreviousMonthReleasedTab = React.forwardRef(({ period, monthOptions, onCountChange, buildingStatuses }, ref) => {
  const gridRef = useRef(null);

  const [prevPer,      setPrevPer]      = useState(() => prevPeriod(period));
  const [inputSearch,  setInputSearch]  = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [rowData,      setRowData]      = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [page,         setPage]         = useState(1);
  const [perPage,      setPerPage]      = useState(20);
  const [totalRows,    setTotalRows]    = useState(0);

  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  const fetchData = useCallback(async ({ p, pp, q = '', per, status = [] }) => {
    if (!per) return;
    setLoading(true);
    try {
      const params = { period: per, page: p, per_page: pp };
      if (q.trim())      params.q                   = q.trim();
      if (status.length) params.project_status_name = status.join(',');
      const res  = await apiAxios.get(BASE_CSD_URL, { params });
      const csds = objectSerializer(res.data?.rows || []);
      setRowData(expandCsds(csds, monthOptions));
      setTotalRows(res.data?.total_rows ?? csds.length);
    } catch {
      toast.error('Failed to load previous month CSD');
    } finally {
      setLoading(false);
    }
  }, [monthOptions]);

  useEffect(() => {
    setPage(1); setInputSearch(''); setStatusFilter([]);
    fetchData({ p: 1, pp: perPage, q: '', per: prevPer, status: [] });
  // refetch from page 1 only when the period picker changes; paging/search fetch on their own
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevPer]);

  useImperativeHandle(ref, () => ({
    getSelectedRows: () => gridRef.current?.api?.getSelectedRows() ?? [],
  }));

  const handleSearch = () => {
    setPage(1);
    fetchData({ p: 1, pp: perPage, q: inputSearch, per: prevPer, status: statusFilter });
  };

  const handleStatusChange = (val) => {
    setStatusFilter(val); setPage(1);
    fetchData({ p: 1, pp: perPage, q: inputSearch, per: prevPer, status: val });
  };

  const handlePageChange = (p) => {
    setPage(p);
    fetchData({ p, pp: perPage, q: inputSearch, per: prevPer, status: statusFilter });
  };

  const handlePerPageChange = (pp) => {
    setPerPage(pp); setPage(1);
    fetchData({ p: 1, pp, q: inputSearch, per: prevPer, status: statusFilter });
  };

  const updateRowMonth = useCallback((rowId, monthIdx) => {
    setRowData((prev) => prev.map((r) => r.id === rowId ? { ...r, month_index: monthIdx } : r));
  }, []);

  const columnDefs = useMemo(() => [
    checkboxColDef, rowNumColDef,
    { headerName: 'Project Name', field: 'project_name',       flex: 2, minWidth: 180 },
    { headerName: 'Job / Q No.', field: 'job_no',               width: 110 },
    { headerName: 'Building',    field: 'building_name',        flex: 1, minWidth: 90 },
    { headerName: 'Status',      field: 'project_status_name',  width: 140 },
    {
      headerName: 'Month', field: 'month_index', width: 130,
      cellRenderer: (p) => (
        <MonthSelectCell value={p.data.month_index} options={monthOptions}
          onChange={(v) => updateRowMonth(p.data.id, v)} />
      ),
    },
    { headerName: 'B/ups', field: 'built_up_weight',   width: 70, type: 'numericColumn',
      valueFormatter: (p) => p.value ? Number(p.value).toFixed(2) : '—' },
    { headerName: 'C/F',   field: 'cold_formed_weight', width: 65, type: 'numericColumn',
      valueFormatter: (p) => p.value ? Number(p.value).toFixed(2) : '—' },
    { headerName: 'SH',    field: 'sheeting_weight',    width: 60, type: 'numericColumn',
      valueFormatter: (p) => p.value ? Number(p.value).toFixed(2) : '—' },
    { headerName: 'Sales Unit', field: 'sales_unit', width: 110 },
  ], [monthOptions, updateRowMonth]);

  return (
    <div className="tw-flex tw-flex-col tw-flex-1 tw-gap-2 tw-min-h-0">
      <div className="tw-flex tw-gap-2 tw-items-center tw-flex-wrap tw-shrink-0">
        <div className="tw-flex tw-items-center tw-gap-1.5">
          <span className="tw-text-xs tw-font-semibold tw-text-gray-600 tw-whitespace-nowrap">Period:</span>
          <MonthYearPicker value={prevPer} onChange={setPrevPer} clearable={false} minWidth={130} />
        </div>
        <MultiStatusSelect value={statusFilter} onChange={handleStatusChange} statuses={buildingStatuses} />
        <SearchInput value={inputSearch} onChange={setInputSearch} onEnter={handleSearch}
          placeholder="Project name, job or quote number…" />
        <SearchBtn onClick={handleSearch} />
        <span className="tw-text-xs tw-text-gray-400 tw-whitespace-nowrap">→ import into <strong>{period}</strong></span>
      </div>

      <div className="ag-theme-quartz tw-flex-1 tw-min-h-0" style={{ position: 'relative' }}>
        <GridOverlay loading={loading} />
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          getRowId={(p) => p.data.id}
          onSelectionChanged={(e) => onCountChange(e.api.getSelectedRows().length)}
          {...GRID_DEFAULTS}
        />
      </div>

      <div className="tw-shrink-0">
        <Pagination
          currentPage={page} totalPages={totalPages} totalResults={totalRows} perPage={perPage}
          onPageChange={handlePageChange} onPageSizeChange={handlePerPageChange}
          pageSizeOptions={[20, 50, 100]} disabled={loading}
        />
      </div>
    </div>
  );
});

PreviousMonthReleasedTab.propTypes = {
  period: PropTypes.string.isRequired,
  monthOptions: PropTypes.arrayOf(PropTypes.shape({
    index: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    firstDay: PropTypes.string.isRequired,
  })).isRequired,
  onCountChange:    PropTypes.func.isRequired,
  buildingStatuses: PropTypes.array.isRequired,
};

// ── Tab pill ───────────────────────────────────────────────────────────────────

const TabPill = ({ id, active, icon: Icon, label, onClick }) => (
  <button
    type="button" onClick={() => onClick(id)}
    className={`tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-border-0 tw-cursor-pointer tw-transition-colors tw-border-b-2 ${
      active
        ? 'tw-text-primary-600 tw-border-primary-500 tw-bg-primary-50/50'
        : 'tw-text-gray-500 tw-border-transparent tw-bg-transparent hover:tw-text-gray-800 hover:tw-bg-gray-50'
    }`}
  >
    <Icon size={13} /> {label}
  </button>
);

TabPill.propTypes = {
  id: PropTypes.string.isRequired,
  active: PropTypes.bool.isRequired,
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

// ── Main modal ─────────────────────────────────────────────────────────────────

const CsdImportModal = ({ show, onHide, period, onSuccess }) => {
  const dispatch = useDispatch();

  const [tab,             setTab]             = useState('buildings');
  const [importing,       setImporting]       = useState(false);
  const [salesCount,      setSalesCount]      = useState(0);
  const [buildingsCount,  setBuildingsCount]  = useState(0);
  const [previousCount,   setPreviousCount]   = useState(0);
  const [buildingStatuses, setBuildingStatuses] = useState([]);

  useEffect(() => {
    dispatch(getBuildingStatuses()).then((action) => {
      const data = action.payload?.data || action.payload || [];
      setBuildingStatuses(Array.isArray(data) ? data : []);
    });
  }, [dispatch]);

  const salesRef     = useRef(null);
  const buildingsRef = useRef(null);
  const previousRef  = useRef(null);

  const monthOptions  = useMemo(() => getMonthOptions(period), [period]);
  const totalSelected = salesCount + buildingsCount + previousCount;

  useEffect(() => {
    if (!show) return;
    setTab('buildings');
    setSalesCount(0); setBuildingsCount(0); setPreviousCount(0);
  }, [show]);

  useEffect(() => {
    document.body.style.overflow = show ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [show]);

  const handleImport = useCallback(async () => {
    const salesRows     = salesRef.current?.getSelectedRows()    ?? [];
    const buildingItems = buildingsRef.current?.getSelectedItems() ?? [];
    const prevRows      = previousRef.current?.getSelectedRows()  ?? [];

    const flatItems = [
      ...salesRows.filter((r) => r.deal_id && r.building_id).map((r) => ({
        deal_id: r.deal_id, building_id: r.building_id, month_index: r.month_index,
        built_up_weight: r.built_up_weight || 0, cold_formed_weight: 0, sheeting_weight: 0,
        trans_date: monthOptions.find((m) => m.index === r.month_index)?.firstDay,
        sales_unit_id: r.sales_unit_id ?? null,
      })),
      ...buildingItems,
      ...prevRows.filter((r) => r.deal_id && r.building_id).map((r) => ({
        deal_id: r.deal_id, building_id: r.building_id, month_index: r.month_index,
        built_up_weight: r.built_up_weight || 0, cold_formed_weight: r.cold_formed_weight || 0,
        sheeting_weight: r.sheeting_weight || 0,
        trans_date: monthOptions.find((m) => m.index === r.month_index)?.firstDay,
        sales_unit_id: r.sales_unit_id ?? null,
      })),
    ];

    if (flatItems.length === 0) { toast.error('No valid buildings selected — check building_id'); return; }

    const byProject = {};
    flatItems.forEach((item) => {
      if (!byProject[item.deal_id]) {
        byProject[item.deal_id] = {
          deal_id: item.deal_id,
          percent_change_of_release: 100,
          sales_unit_id: item.sales_unit_id ?? null,
          monthlies: [],
        };
      }
      byProject[item.deal_id].monthlies.push({
        building_id: item.building_id, month_index: item.month_index,
        built_up_weight: item.built_up_weight, cold_formed_weight: item.cold_formed_weight,
        sheeting_weight: item.sheeting_weight, trans_date: item.trans_date,
      });
    });

    const items = Object.values(byProject);
    setImporting(true);
    try {
      await dispatch(importCsdFromSource({ period, items })).unwrap();
      toast.success(`Imported ${items.length} project(s) into ${period}`);
      onSuccess?.();
      onHide();
    } catch (e) {
      toast.error(e?.error || e?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  }, [monthOptions, period, dispatch, onSuccess, onHide]);

  if (!show) return null;

  const TABS = [
    { id: 'buildings', icon: MdBusiness,            label: 'All projects' },
    { id: 'sales',     icon: MdOutlineImportExport, label: 'Sales Released' },
    { id: 'previous',  icon: MdHistory,             label: 'Previous Month Pending' },
  ];

  return ReactDOM.createPortal(
    <div className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-p-4 tw-bg-black tw-bg-opacity-50" style={{ zIndex: 99999 }}>
      <div className="tw-bg-white tw-rounded tw-shadow-2xl tw-w-full tw-flex tw-flex-col tw-overflow-hidden" style={{ maxWidth: 1020, height: '90vh' }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="tw-px-5 tw-py-3.5 tw-bg-slate-50 tw-border-b tw-border-slate-200 tw-shrink-0 tw-flex tw-items-center tw-justify-between">
          <div>
            <h3 className="tw-font-semibold tw-text-sm tw-text-slate-800 tw-mb-0">New CSD Forecast — Import Data</h3>
            <p className="tw-text-xs tw-text-slate-400 tw-mt-0.5">
              Period: <strong>{period}</strong> &nbsp;→&nbsp; Months: {monthOptions.map((m) => m.label).join(', ')}
            </p>
          </div>
          <button onClick={onHide} className="tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-slate-400 hover:tw-text-slate-600 tw-p-1 tw-rounded-sm">
            <FaTimes size={13} />
          </button>
        </div>

        {/* Tabs */}
        <div className="tw-flex tw-border-b tw-border-slate-200 tw-shrink-0">
          {TABS.map((t) => <TabPill key={t.id} id={t.id} active={tab === t.id} icon={t.icon} label={t.label} onClick={setTab} />)}
        </div>

        {/* Body — all tabs always mounted to preserve grid selection */}
        <div className="tw-flex-1 tw-flex tw-flex-col tw-overflow-hidden">
          <div style={{ display: tab === 'sales'     ? 'flex' : 'none', flexDirection: 'column', flex: 1, overflow: 'hidden', padding: '1rem' }}>
            <SalesReleasedTab ref={salesRef}    period={period} monthOptions={monthOptions} onCountChange={setSalesCount}     buildingStatuses={buildingStatuses} />
          </div>
          <div style={{ display: tab === 'buildings' ? 'flex' : 'none', flexDirection: 'column', flex: 1, overflow: 'hidden', padding: '1rem' }}>
            <AllProjectsTab      ref={buildingsRef} period={period} monthOptions={monthOptions} onCountChange={setBuildingsCount} buildingStatuses={buildingStatuses} />
          </div>
          <div style={{ display: tab === 'previous'  ? 'flex' : 'none', flexDirection: 'column', flex: 1, overflow: 'hidden', padding: '1rem' }}>
            <PreviousMonthReleasedTab  ref={previousRef} period={period} monthOptions={monthOptions} onCountChange={setPreviousCount} buildingStatuses={buildingStatuses} />
          </div>
        </div>

        {/* Footer */}
        <div className="tw-px-5 tw-py-3 tw-bg-slate-50 tw-border-t tw-border-slate-200 tw-shrink-0 tw-flex tw-items-center tw-justify-between">
          <span className="tw-text-xs tw-text-slate-500">
            {totalSelected > 0
              ? <span className="tw-text-primary-600 tw-font-semibold">{totalSelected} building row(s) selected for import</span>
              : 'Select building rows, set the month, then click Import'}
          </span>
          <div className="tw-flex tw-gap-2">
            <Button variant="secondary" onClick={onHide} disabled={importing}>
              Cancel
            </Button>
            <Button
              variant="primary"
              leftIcon={FaPlus}
              onClick={handleImport}
              loading={importing}
              disabled={totalSelected === 0}
            >
              Import {totalSelected > 0 && `(${totalSelected})`}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

CsdImportModal.propTypes = {
  show:      PropTypes.bool.isRequired,
  onHide:    PropTypes.func.isRequired,
  period:    PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
};

CsdImportModal.defaultProps = { onSuccess: null };

export default CsdImportModal;
